import { SelectOption } from "atoms/forms/select";
import { useState } from "react";
import { useHistory, useLocation } from "react-router";
import NumberUtils from "utilities/number-utils";
import { RouteUtils } from "utilities/route-utils";

/*
    This is equivilent in logic to the usePagedResults hook, but instead of maintaining its own state,
    it uses the state saved in the URL search string (eg, ?skip=0&take=10).

    If you use this in combination with other filters or sorting options, it is recommended
    that those too are also stored in the params rather than in React's state.
*/

const DEFAULT_TAKE_SIZE = 10;
export interface UsePagedResultsFromParamsData {
    currentPage: number;
    numberOfPages: number;
    onPageLast: VoidFunction;
    onPageNext: VoidFunction;
    onPageSizeChange: (selectedOption?: SelectOption<number, number>) => void;
    onSelectPage: (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    resetPagination: VoidFunction;
    rowCount: number;
    setRowCount: (count: number) => void;
    skip: number;
    take: number;
    setSkip: (count: number) => void;
    onSetPage: (page: number) => void;
    noRecordsRemaining: boolean;
}

export default function usePagedResultsFromParams(
    defaultTake = DEFAULT_TAKE_SIZE,
    paramIdToAppend = ""
): UsePagedResultsFromParamsData {
    const { pathname, search } = useLocation();
    const { replace } = useHistory();

    // Stores multiple skip/take params to allow for multiple panels/states in URL
    const skipKey = paramIdToAppend ? `skip_${paramIdToAppend}` : "skip";
    const takeKey = paramIdToAppend ? `take_${paramIdToAppend}` : "take";

    const currentParams = RouteUtils.queryStringToObject<{
        [skipKey]: number;
        [takeKey]: number;
        [key: string]: any;
    }>(search);

    const { [skipKey]: skip = 0, [takeKey]: take = defaultTake } =
        currentParams;

    const [rowCount, setRowCount] = useState(0);

    const setSkip = (newSkip: number) => {
        const { [skipKey]: prevSkip, ...otherParams } =
            RouteUtils.queryStringToObject<{ [key: string]: any }>(
                window.location.search
            );
        replace(
            RouteUtils.appendQueryParams(pathname, {
                ...otherParams,
                [skipKey]: newSkip,
            })
        );
    };

    const setTake = (newTake: number) => {
        const { take: prevTake, ...otherParams } = currentParams;
        replace(
            RouteUtils.appendQueryParams(pathname, {
                ...otherParams,
                [takeKey]: newTake,
                [skipKey]: 0,
            })
        );
    };

    const currentPage = (skip + take) / take;
    const numberOfPages = Math.ceil(rowCount / take);

    const onPageLast = () => {
        if (currentPage === 1) {
            return;
        }

        setSkip(skip - take);
    };

    const onPageNext = () => {
        console.log(currentPage, numberOfPages, skip, take);
        if (currentPage === numberOfPages) {
            return;
        }

        setSkip(skip + take);
    };

    const onPageSizeChange = (
        selectedOption?: SelectOption<number, number>
    ) => {
        const value = selectedOption?.value;

        if (value == null) {
            return;
        }

        setTake(value);
    };

    const onSelectPage = (
        e?: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        const selectedPage = NumberUtils.parseInt(e!.currentTarget.value);

        if (selectedPage == null) {
            return;
        }

        if (selectedPage <= 1 && selectedPage >= numberOfPages) {
            return;
        }
        setSkip((selectedPage - 1) * take);
    };

    const onSetPage = (selectedPage: number) => {
        if (selectedPage == null) {
            return;
        }

        if (selectedPage <= 1 && selectedPage >= numberOfPages) {
            return;
        }
        setSkip((selectedPage - 1) * take);
    };

    const resetPagination = () => {
        setSkip(0);
    };

    const noRecordsRemaining = currentPage === numberOfPages;

    return {
        currentPage,
        numberOfPages,
        onPageLast,
        onPageNext,
        onPageSizeChange,
        onSelectPage,
        onSetPage,
        resetPagination,
        rowCount,
        setRowCount,
        skip,
        take,
        setSkip,
        noRecordsRemaining,
    };
}
